import { useState } from 'react';
import { Mark } from './Mark.tsx';

/**
 * Enrol a phone as a node.
 *
 * The desktop shell does this with a machine GUID from the registry.
 * A phone has no equivalent, so it generates an install id and keeps
 * it: reinstalling the app produces a new one, which is correct, as
 * it is a new install and should be enrolled again.
 */
const INSTALL_KEY = 'kr.install';

const installId = (): string => {
  const seen = localStorage.getItem(INSTALL_KEY);
  if (seen) return seen;
  const made = crypto.randomUUID();
  localStorage.setItem(INSTALL_KEY, made);
  return made;
};

export const Enrol = ({ name, onEnrolled, onBack }: {
  name: string;
  onEnrolled: () => void;
  onBack: () => void;
}) => {
  const [code, setCode] = useState('');
  const [label, setLabel] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const submit = async () => {
    const clean = code.trim().toUpperCase().replace(/[^0-9A-Z]/g, '');
    if (clean.length < 6) {
      setError('That code looks too short.');
      return;
    }

    setBusy(true);
    setError('');

    try {
      const r = await fetch('/devices/enrol', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          code: clean,
          machineGuid: installId(),
          hostname: label.trim() || 'Phone',
          appVersion: 'pwa',
          deviceType: 'mobile',
        }),
      });

      if (!r.ok) {
        const body = await r.json().catch(() => ({}));
        setError(body.error === 'code_used'
          ? 'That code has already been used.'
          : body.error === 'code_expired'
            ? 'That code has expired. Ask for a new one.'
            : 'That code was not accepted.');
        return;
      }

      // The API returns deviceToken. Destructuring `token` stored the
      // string "undefined", so every phone enrolment burned a
      // one-time code and produced a device that could never
      // authenticate.
      const { deviceToken: issued } = await r.json();
      if (!issued) {
        setError('The server did not return a credential. Try again.');
        return;
      }
      // The same place the shell keeps it, so everything downstream
      // treats this phone exactly like a counter screen.
      localStorage.setItem('kr.device', issued);
      onEnrolled();
    } catch {
      setError('Could not reach the server. Check your connection.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <main className="loginWrap">
      <div className="loginCard">
        <Mark size={42} />
        <h1>{name}</h1>
        <p className="mono loginHost">{window.location.hostname}</p>
        <p className="mono">Set up this phone</p>

        {error && <p className="err">{error}</p>}

        <label>
          <span className="mono">Enrolment code</span>
          <input
            className="otp"
            autoCapitalize="characters"
            autoCorrect="off"
            maxLength={12}
            value={code}
            onChange={(e) => setCode(e.target.value)}
          />
        </label>

        <label>
          <span className="mono">Name this phone</span>
          <input
            value={label}
            placeholder="e.g. Riverside manager"
            onChange={(e) => setLabel(e.target.value)}
          />
        </label>

        <button
          className="primary"
          disabled={busy}
          onClick={() => void submit()}
        >
          {busy ? 'Setting up...' : 'Set up'}
        </button>

        <button className="linkish" onClick={onBack}>
          I have an account instead
        </button>
      </div>
    </main>
  );
};
